/**
 * The Z S MERCH shop — a third entry point, alongside the dashboard and the till.
 *
 * ── 🔴 Why its own bundle ────────────────────────────────────────────────────────────────────
 * The same reason the till has one, one step further. A shopper is an anonymous member of the
 * public: serving them the dashboard bundle would ship the whole business — pricing, marketplaces,
 * margins, the back office nav — to anyone who opens the shop. It is only JavaScript rather than
 * data, but it is a complete map of the product, and a storefront is the one surface that is
 * deliberately handed to strangers.
 *
 * It is also the surface where load time is revenue, and the dashboard bundle would be dead weight
 * on a phone on mobile data — which is how most of this shop's customers will arrive.
 *
 * ⚠️ Every price and stock figure here comes from /api/shop/*, which projects field by field. This
 * file must never gain a call to a session-gated endpoint: the store has no session.
 *
 * ── 🔴 The basket carries ids and quantities. Nothing else. ─────────────────────────────────
 * Prices are shown from the catalogue for the customer's benefit, but the checkout request sends
 * only what was chosen and how many; the server re-prices every line at that moment. A basket
 * that carried its own prices would be a discount code nobody issued.
 */
import { reloadOnChunkError } from "./chunk-reload";
import { Component, StrictMode, Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import "./store.css";
import "./store-content.css";

import type { Account, CategorySummary, NavNode, NavResponse, OrderView, ShopConfig } from "./store/types";
import { Arrow, Check, freeDeliveryLine, get, rands, thresholdRands } from "./store/shared";
import { lineRoute, useBasket, type Basket } from "./store/basket";
import { Go, PENDING_CODE_KEY, pathFor, useRoute, type Route } from "./store/route";
import { setSeo } from "./store/seo";
import { CartDrawer, Header } from "./store/Header";
import { Footer } from "./store/Footer";
import { AppBanner } from "./store/AppPromo";
import { HomePage } from "./store/Home";
import { ClearanceRoute, CollectionRoute, CollectionsPage, DepartmentRoute, NotFoundPage, SearchRoute } from "./store/Pages";
import { ProductPage } from "./store/Product";
import { CollectLink, useCollect } from "./store/collect";
import { ContactPage } from "./store/Contact";
import { AboutPage } from "./store/About";
import { FaqPage } from "./store/Faq";
import { ChannelButton, SubscribePopup } from "./store/Subscribe";
import { adsPageView, adsPurchase, configureAds, trackToAds } from "./store/ads";
import { onTrack, track } from "./store/track";
import { sizedSrc } from "./store/img";
import { ConsentBanner } from "./store/ConsentBanner";
/** Only someone holding a review-request link opens this — its own chunk. */
const ReviewOrderPage = lazy(() => import("./store/ReviewOrder").catch(reloadOnChunkError));
/** The store policies' legal text — its own chunk. */
/** My orders / order detail / Track an order - its own chunk. */
const OrdersPage = lazy(() => import("./store/Orders").then((m) => ({ default: m.OrdersPage })).catch(reloadOnChunkError));
const TrackPage = lazy(() => import("./store/Orders").then((m) => ({ default: m.TrackPage })).catch(reloadOnChunkError));
const PolicyPage = lazy(() => import("./store/Policy").catch(reloadOnChunkError));
/** Bundles & Kits (list + one bundle) — its own chunk. */
const BundlesPage = lazy(() => import("./store/Bundles").then((m) => ({ default: m.BundlesPage })).catch(reloadOnChunkError));
const BundlePage = lazy(() => import("./store/Bundles").then((m) => ({ default: m.BundlePage })).catch(reloadOnChunkError));
/** Buying guides (list + one guide) - its own chunk. */
const GuidesPage = lazy(() => import("./store/Guides").then((m) => ({ default: m.GuidesPage })).catch(reloadOnChunkError));
const GuidePage = lazy(() => import("./store/Guides").then((m) => ({ default: m.GuidePage })).catch(reloadOnChunkError));
/** Holiday sale pages (/s, /s/<slug>) — their own chunk. */
const CampaignPage = lazy(() => import("./store/Campaign").then((m) => ({ default: m.CampaignPage })).catch(reloadOnChunkError));
const CampaignsPage = lazy(() => import("./store/Campaign").then((m) => ({ default: m.CampaignsPage })).catch(reloadOnChunkError));
import { AddressSearch, type PickedAddress } from "./AddressSearch";


/** What /api/shop/quote answers: the server's delivery decision, projected for display only. */
type DeliveryQuote = {
  mode: "courier" | "zone" | "refused";
  options: Array<{ id: string; provider: string; name: string; priceCents: number; eta: string | null }>;
  feeCents: number | null;
  message: string | null;
  /** Refused for size or weight — a free-delivery code does not override it (server rule, mirrored). */
  oversize?: boolean;
};
/** A best guess from the postal code, to pre-select the province. It never decides the fee. */
function provinceFromPostcode(postcode: string): string | null {
  const n = Number(postcode.replace(/\D/g, ""));
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n <= 299) return "Gauteng";
  if (n <= 499) return "North West";
  if (n <= 999) return "Limpopo";
  if (n <= 1399) return "Mpumalanga";
  if (n <= 2199) return "Gauteng";
  if (n <= 2899) return "North West";
  if (n <= 4999) return "KwaZulu-Natal";
  if (n <= 6499) return "Eastern Cape";
  if (n <= 8299) return "Western Cape";
  if (n <= 8999) return "Northern Cape";
  if (n <= 9999) return "Free State";
  return null;
}

/* ── Basket ────────────────────────────────────────────────────────────────────────────────── */
function BasketPage({ basket, go, config, trade = false }: { basket: Basket; go: (r: Route) => void; config: ShopConfig | null; trade?: boolean }) {
  const collect = useCollect(basket.lines);
  const point = collect?.point ?? config?.collect ?? null;
  /** One begin_checkout when the page opens with something in the basket (not on every edit). */
  const beganCheckout = useRef(false);
  useEffect(() => {
    if (beganCheckout.current || basket.lines.length === 0) return;
    beganCheckout.current = true;
    const ids = basket.lines.map((l) => l.feedId).filter((x): x is string => Boolean(x));
    track("begin_checkout", { valueCents: basket.subtotalCents, quantity: basket.count, ...(ids.length ? { contentIds: ids } : {}) });
  }, [basket.lines, basket.subtotalCents, basket.count]);
  if (basket.lines.length === 0) {
    return <div className="empty"><h3>Your basket is empty</h3><p>Everything you add will wait here, even if you close the tab.</p><Go to={{ name: "home" }} go={go} className="btn btn-quiet">Browse the shop</Go></div>;
  }
  return (
    <div className="wrap two-col">
      <div>
        <h1 style={{ fontSize: "clamp(1.7rem, 1.2rem + 1.6vw, 2.4rem)", marginBottom: "1rem" }}>Your basket</h1>
        {basket.lines.map((l) => (
          <div className="line" key={l.variantId}>
            <div className="line-img">{l.image && <img src={sizedSrc(l.image, 160)} alt="" loading="lazy" />}</div>
            <div>
              <h3><Go to={lineRoute(l)} go={go} style={{ textDecoration: "none" }}>{l.title}</Go>{l.variantTitle && <span className="sub2"> — {l.variantTitle}</span>}</h3>
              {l.sku && <div className="sku money">{l.sku}</div>}
              {(() => {
                const c = collect?.lines.find((x) => x.variantId === l.variantId);
                return c ? <div className="fine" style={{ marginTop: ".25rem" }}>{c.when === "none" ? c.label : <CollectLink label={c.label} mapUrl={point?.mapUrl} />}</div> : null;
              })()}
              <div className="qty" style={{ marginTop: ".5rem" }}>
                <button onClick={() => basket.setQty(l.variantId, l.quantity - 1)} aria-label="Fewer">−</button><span>{l.quantity}</span><button onClick={() => basket.setQty(l.variantId, Math.min(500, l.quantity + 1))} aria-label="More">+</button>
              </div>
            </div>
            <div className="line-right">
              <b className="money">{rands((l.priceCents ?? 0) * l.quantity)}</b>
              {l.quantity > 1 && <span className="sub2 money" style={{ fontSize: ".8rem", color: "var(--ink-mute)" }}>{rands(l.priceCents)} each</span>}
              <button className="remove" onClick={() => basket.setQty(l.variantId, 0)}>Remove</button>
            </div>
          </div>
        ))}
      </div>
      <aside className="summary">
        <h2>Summary</h2>
        <div className="row"><span>Items ({basket.count})</span><span className="money">{rands(basket.subtotalCents)}</span></div>
        {/* Never "free" from the raw subtotal: discount, address and courier coverage are decided at checkout. */}
        <div className="row"><span>Delivery</span><span className="money">priced at checkout</span></div>
        <div className="row total"><span>Items total</span><span className="money">{rands(basket.subtotalCents)}</span></div>
        <button className="btn btn-brass" style={{ width: "100%", marginTop: "1rem" }} onClick={() => go({ name: "checkout" })}>Checkout <Arrow /></button>
        {(() => {
          /* Free delivery is retail-only (owner, 30 Oct): trade accounts never see the line. */
          const line = trade ? null : freeDeliveryLine(config?.freeDeliveryOverCents, basket.subtotalCents);
          return line ? <p className="fine" role="status" style={{ color: "var(--ink)" }}>{line}</p> : null;
        })()}
        {collect?.collectable && <p className="fine"><CollectLink label={collect.readyLabel} mapUrl={point?.mapUrl} /> · free</p>}
        <p className="fine">Collection{point ? ` at ${point.name}` : ""} is free. Every line is re-checked against live stock when you pay.</p>
      </aside>
    </div>
  );
}

/* ── Checkout ──────────────────────────────────────────────────────────────────────────────── */
function Checkout({ basket, go, config, account }: { basket: Basket; go: (r: Route) => void; config: ShopConfig | null; account: Account | null }) {
  const [name, setName] = useState(account?.name ?? "");
  const [email, setEmail] = useState(account?.email ?? "");
  const [phone, setPhone] = useState(account?.phone ?? "");
  /** A customer who signs in mid-checkout gets their details filled, never overwritten if typed. */
  useEffect(() => {
    if (!account) return;
    setName((v) => v || account.name);
    setEmail((v) => v || account.email);
    setPhone((v) => v || account.phone || "");
  }, [account]);
  const [fulfilment, setFulfilment] = useState<"deliver" | "collect">("deliver");
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [suburb, setSuburb] = useState("");
  const [city, setCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [province, setProvince] = useState("");
  /**
   * A Google pick fills every field at once (one render, so the quote effect below aborts the old
   * request and quotes the new address once). Every field stays editable afterwards. An unmatched
   * province is left blank, so the postcode guess below fills it.
   */
  const fillFromPick = (a: PickedAddress) => {
    setLine1(a.street || a.formatted.split(",")[0] || "");
    setLine2(a.unit);
    // A small town has no suburb in Google; the town itself is what a courier expects there.
    setSuburb(a.suburb || a.city);
    setCity(a.city);
    setPostcode(a.postcode);
    setProvince(a.province);
  };
  /**
   * The server's delivery decision for this basket and address: live courier options, the zone
   * fee, or "not offered online" (with the sentence to show). The browser only displays it — the
   * checkout decides again from scratch and never takes a fee from this page.
   */
  const [quote, setQuote] = useState<DeliveryQuote | null>(null);
  const [quoting, setQuoting] = useState(false);
  /** The last quote request failed (429 / 5xx / network): say so and offer Retry, never a silent blank. */
  const [quoteFailed, setQuoteFailed] = useState(false);
  const [courierOptionId, setCourierOptionId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  /** Pre-select the province from the postcode, but never overwrite one the customer chose. */
  useEffect(() => {
    if (province) return;
    const guess = provinceFromPostcode(postcode);
    if (guess) setProvince(guess);
  }, [postcode, province]);

  /**
   * Quote once the address is complete, debounced so typing a postcode does not fire a courier
   * call per keystroke. Any change to the address clears the chosen option: a fee for the old
   * address must not be carried to the new one.
   */
  /** Bumped when the server says the delivery changed (409), to quote the same address again. */
  const [requote, setRequote] = useState(0);
  useEffect(() => {
    /**
     * 🔴 The old quote is cleared THE MOMENT the address changes, and Pay stays disabled through
     * the debounce as well as the request (2026-10-10 review): until then the previous address's
     * fee sat on screen, payable, for the 600 ms before the new request even started.
     */
    setCourierOptionId(null);
    setQuote(null);
    setQuoteFailed(false);
    if (fulfilment !== "deliver" || !line1.trim() || !suburb.trim() || !city.trim() || !province || postcode.replace(/\D/g, "").length < 4) { setQuoting(false); return; }
    setQuoting(true);
    /**
     * 🔴 The in-flight request is ABORTED, not just the timer cancelled (2026-09-08 audit).
     *
     * The cleanup cleared the debounce timer and left any request already in the air running. So a
     * shopper who corrected a Johannesburg address to Cape Town before the first quote returned got
     * the Johannesburg response landing LAST — overwriting `quote` and `courierOptionId` — and that
     * stale option id is what `pay()` posts. They were charged a delivery fee quoted for an address
     * they are not at.
     */
    const ac = new AbortController();
    const t = window.setTimeout(async () => {
      try {
        const r = await fetch("/api/shop/quote", {
          method: "POST",
          headers: { "content-type": "application/json" },
          signal: ac.signal,
          body: JSON.stringify({ items: basket.lines.map((l) => ({ variantId: l.variantId, quantity: l.quantity })), address: { street: line1, suburb, city, province, postcode } }),
        });
        const d = r.ok ? ((await r.json()) as DeliveryQuote) : null;
        if (ac.signal.aborted) return;
        setQuote(d);
        setQuoteFailed(d === null);
        if (d?.options?.[0]) setCourierOptionId(d.options[0].id);
      } catch {
        /** ⚠️ An abort lands here too, and must NOT blank a newer quote that has already arrived. */
        if (!ac.signal.aborted) { setQuote(null); setQuoteFailed(true); }
      } finally {
        if (!ac.signal.aborted) setQuoting(false);
      }
    }, 600);
    return () => {
      window.clearTimeout(t);
      ac.abort();
    };
  }, [fulfilment, line1, suburb, city, province, postcode, basket.lines, requote]);
  const collect = useCollect(basket.lines);
  const point = collect?.point ?? config?.collect ?? null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  /**
   * Pay by EFT — shown only when the SERVER offered it (config.eft: an approved wholesale buyer and
   * complete bank details). The server refuses it again on checkout for anyone else.
   */
  const eftOffered = Boolean(config?.eft);
  /**
   * Pay at the shop (cash on collection) — shown only when the SERVER offered it (an approved
   * wholesale buyer) AND the customer chose Collect. The server refuses it again for anyone else.
   */
  const shopOffered = Boolean(config?.payAtShop) && fulfilment === "collect";
  const [payMethod, setPayMethod] = useState<"card" | "eft" | "shop">("card");
  const payEft = eftOffered && payMethod === "eft";
  const payShop = shopOffered && payMethod === "shop";
  const payOffline = payEft || payShop;
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState<{ code: string; itemsOffCents: number; freeDelivery: boolean; description: string } | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  /** Preview only: the server decides the real amount again when the customer pays. */
  const tryCode = async (codeToTry?: string) => {
    const value = codeToTry ?? code;
    setCodeError(null);
    if (!value.trim()) return;
    const r = await fetch("/api/shop/discount", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code: value, email, items: basket.lines.map((l) => ({ variantId: l.variantId, quantity: l.quantity })) }),
    }).catch(() => null);
    const d = r ? ((await r.json()) as { ok?: boolean; code?: string; itemsOffCents?: number; freeDelivery?: boolean; description?: string; error?: string }) : null;
    if (r?.ok && d?.ok) setDiscount({ code: d.code!, itemsOffCents: d.itemsOffCents ?? 0, freeDelivery: !!d.freeDelivery, description: d.description ?? "" });
    else { setDiscount(null); setCodeError(d?.error ?? "That code is not valid."); }
  };

  /** A code shared as a link (see PENDING_CODE_KEY) fills the box and applies itself, once. */
  useEffect(() => {
    let pending: string | null = null;
    try {
      pending = sessionStorage.getItem(PENDING_CODE_KEY);
    } catch {
      pending = null;
    }
    if (!pending || basket.lines.length === 0) return;
    try { sessionStorage.removeItem(PENDING_CODE_KEY); } catch { /* nothing to clean up if storage is unavailable */ }
    setCode(pending);
    void tryCode(pending);
    // Fires once, when the basket first has lines to price the code against.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basket.lines.length > 0]);

  if (basket.lines.length === 0 && !saved) {
    return <div className="empty"><h3>Nothing to check out</h3><Go to={{ name: "home" }} go={go} className="btn btn-quiet">Browse the shop</Go></div>;
  }

  const chosen = quote?.mode === "courier" ? (quote.options.find((o) => o.id === courierOptionId) ?? null) : null;
  /** null = not priced yet, or not offered — never shown or added as R0. */
  const deliveryFee: number | null = fulfilment !== "deliver" ? 0 : chosen ? chosen.priceCents : quote?.mode === "zone" ? quote.feeCents : null;
  const off = discount?.itemsOffCents ?? 0;
  /**
   * Free delivery on retail orders of R2,000 or more (owner, 2026-10-30): goods after the code's discount, retail only
   * (config is null for wholesale). A preview — the server re-decides it from its own prices.
   */
  const overThreshold = Boolean(config?.freeDeliveryOverCents) && basket.subtotalCents - off >= (config?.freeDeliveryOverCents ?? Infinity);
  /**
   * The SAME rule as the server (shop-checkout.ts): a free-delivery CODE is R0 even when the
   * courier could not quote, except for an oversize basket; the R2,000 threshold is R0 only when
   * delivery can actually be done (a courier or zone quote) — a refused quote stays refused.
   */
  const codeFreeOk = Boolean(discount?.freeDelivery) && quote !== null && !(quote.mode === "refused" && quote.oversize);
  const thresholdFreeOk = overThreshold && quote !== null && quote.mode !== "refused";
  const freeOk = codeFreeOk || thresholdFreeOk;
  const deliveryRefused = fulfilment === "deliver" && quote?.mode === "refused" && !freeOk;
  const canPay = fulfilment === "collect"
    ? Boolean(collect?.collectable)
    : !quoting && quote !== null && (freeOk || (deliveryFee !== null && !deliveryRefused));
  /** Why Pay is greyed out, in words — a disabled button with no reason is a dead end. */
  const payBlockedReason: string | null = canPay
    ? null
    : fulfilment === "collect"
      ? collect ? "Not everything in this basket can be collected — reduce the quantity, or choose delivery." : "Checking what can be collected…"
      : quoting ? "Getting delivery prices for your address…"
        : quoteFailed ? "We couldn't price delivery just now — press Retry, or choose Collect."
          : deliveryRefused ? "Delivery is not available online for this order — choose Collect, or WhatsApp us."
            : "Enter your full delivery address to see delivery prices.";
  const delivery = fulfilment === "deliver" && freeOk ? 0 : (deliveryFee ?? 0);
  const total = basket.subtotalCents - off + delivery;

  const pay = async () => {
    setBusy(true); setError(null);
    try {
      const r = await fetch("/api/shop/checkout", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          items: basket.lines.map((l) => ({ variantId: l.variantId, quantity: l.quantity })),
          name, email, phone, fulfilment, note,
          address: fulfilment === "deliver" ? { line1, line2, suburb, city, postcode, province } : undefined,
          courierOptionId: chosen?.id,
          code: discount?.code ?? (code.trim() || undefined),
          payMethod: payEft ? "eft" : payShop ? "shop" : undefined,
        }),
      });
      const body = (await r.json()) as { ok?: boolean; url?: string; error?: string; checkoutId?: string; requote?: boolean; eft?: boolean; placed?: boolean; key?: string; reference?: string };
      /** `key`: the order page's secret (the reference is not one). `reference` only for an older server. */
      const orderKey = body.key ?? body.reference;
      if (r.ok && (body.eft || body.placed) && body.checkoutId && orderKey) {
        /** EFT / pay at the shop: the order is placed (awaiting payment, stock held). Its page shows how to pay. */
        basket.clear();
        go({ name: "order", id: body.checkoutId, ref: orderKey, cancelled: false });
        return;
      }
      if (r.status === 409 && body.requote) {
        /** The courier's answer changed between the preview and Pay: show the new options. */
        setError(body.error ?? "Delivery prices changed — please choose again.");
        setRequote((n) => n + 1);
        return;
      }
      if (r.ok && body.url) {
        /**
         * 🔴 The basket is NOT cleared here (2026-09-08 audit). The comment that used to sit on
         * this line claimed it was "cleared only once the provider has agreed to take the payment"
         * — but `body.url` is merely the redirect TO the provider. Nothing has been paid yet.
         *
         * So a shopper who reached the gateway and pressed Back — a declined card, the wrong card,
         * second thoughts — came back to an empty shop with no basket and no order, and had to
         * find every item again. The basket now survives that, and is cleared on the order page
         * once the payment is actually confirmed. See `Order`.
         */
        window.location.assign(body.url);
        return;
      }
      if (r.status === 409 && body.checkoutId) {
        basket.clear();
        setSaved(body.error ?? "Your order request has been saved.");
        return;
      }
      setError(body.error ?? "Something went wrong. Nothing has been charged.");
    } catch {
      setError("We could not reach the shop. Nothing has been charged — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (saved) {
    return <div className="empty"><div className="paid-mark" style={{ margin: "0 auto 1rem" }}><Check size={28} /></div><h3>Order request saved</h3><p style={{ maxWidth: "48ch", margin: "0 auto 1rem" }}>{saved}</p><Go to={{ name: "home" }} go={go} className="btn btn-quiet">Back to the shop</Go></div>;
  }

  return (
    <div className="wrap two-col">
      <form onSubmit={(e) => { e.preventDefault(); void pay(); }}>
        <h1 style={{ fontSize: "clamp(1.7rem, 1.2rem + 1.6vw, 2.4rem)", marginBottom: "1.2rem" }}>Checkout</h1>
        {error && <div className="alert bad" role="alert">{error}</div>}

        <h2 style={{ fontSize: "1.15rem", marginBottom: ".8rem" }}>Who is this for?</h2>
        <label className="field"><span>Name</span><input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required /></label>
        <div className="field-row">
          <label className="field"><span>Email</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required /></label>
          <label className="field"><span>Phone (for the driver)</span><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" /></label>
        </div>

        <h2 style={{ fontSize: "1.15rem", margin: "1.2rem 0 .8rem" }}>How would you like it?</h2>
        <div className="choice">
          <label><input type="radio" name="f" checked={fulfilment === "deliver"} onChange={() => setFulfilment("deliver")} /><b>Deliver to me</b><small>2–3 working days to main centres, 3–5 to outlying areas · priced for your address below</small></label>
          <label><input type="radio" name="f" checked={fulfilment === "collect"} onChange={() => setFulfilment("collect")} /><b>Collect{point ? ` at ${point.name}` : " in store"}</b><small>{point?.address ?? config?.collectAt ?? ""} · free</small></label>
        </div>
        {fulfilment === "collect" && collect && (
          <p className="fine" role="status" style={{ marginTop: ".4rem", color: collect.collectable ? "var(--ink)" : "var(--bad)" }}>
            {collect.collectable
              ? <CollectLink label={collect.readyLabel} mapUrl={point?.mapUrl} />
              : `${collect.lines.find((l) => l.when === "none")?.label ?? "Not available to collect"} for everything in this basket — reduce the quantity, or choose delivery.`}
          </p>
        )}

        {fulfilment === "deliver" && (
          <>
            <AddressSearch endpoint="/api/shop/address" className="field" label="Street address" value={line1} onChange={setLine1} onPick={fillFromPick} autoComplete="address-line1" placeholder="Start typing your address" required />
            <label className="field"><span>Complex, unit or building (optional)</span><input value={line2} onChange={(e) => setLine2(e.target.value)} autoComplete="address-line2" /></label>
            <label className="field"><span>Suburb</span><input value={suburb} onChange={(e) => setSuburb(e.target.value)} autoComplete="address-level3" required /></label>
            <div className="field-row">
              <label className="field"><span>City or town</span><input value={city} onChange={(e) => setCity(e.target.value)} autoComplete="address-level2" required /></label>
              <label className="field"><span>Postal code</span><input value={postcode} onChange={(e) => setPostcode(e.target.value)} autoComplete="postal-code" inputMode="numeric" required /></label>
            </div>
            <label className="field"><span>Province</span>
              <select value={province} onChange={(e) => setProvince(e.target.value)} required>
                <option value="">Choose…</option>
                {(config?.provinces ?? []).map((p) => <option key={p}>{p}</option>)}
              </select>
            </label>
            {!quoting && quoteFailed && (
              <div className="fieldset" role="alert" style={{ marginTop: ".4rem" }}>
                <p className="fine" style={{ color: "var(--ink)", margin: 0 }}>We couldn't price delivery just now.</p>
                <p className="fine" style={{ color: "var(--ink-soft)" }}>Try again in a moment — or choose Collect{point ? ` at ${point.name}` : ""}, which is always free.</p>
                <button type="button" className="btn btn-quiet" style={{ minHeight: 40, padding: ".4rem .9rem" }} onClick={() => setRequote((n) => n + 1)}>Retry</button>
              </div>
            )}
            {(quoting || quote) && (
              <div className="fieldset" style={{ marginTop: ".4rem" }}>
                <h3>Delivery options for this address</h3>
                {quoting && <p className="fine" style={{ color: "var(--ink-mute)" }}>Getting live courier prices…</p>}
                {!quoting && quote?.mode === "refused" && freeOk && (
                  <p className="fine" role="status" style={{ color: "var(--good)" }}>Free delivery with {discount?.code} — we will book the courier for you.</p>
                )}
                {!quoting && quote?.mode === "refused" && !freeOk && (
                  <p className="fine" role="status" style={{ color: "var(--ink)" }}>{quote.message ?? "This order needs a delivery quote — collect in store or WhatsApp us."}</p>
                )}
                {!quoting && quote?.mode === "zone" && quote.feeCents !== null && (
                  <p className="fine" style={{ color: "var(--ink-soft)" }}>Standard {province === "Gauteng" ? "Gauteng" : "rest-of-SA"} delivery · <b className="money">{rands(quote.feeCents)}</b></p>
                )}
                {!quoting && quote && quote.options.map((o) => (
                  <label key={o.id} className="consent" style={{ alignItems: "center" }}>
                    <input type="radio" name="courier" checked={courierOptionId === o.id} onChange={() => setCourierOptionId(o.id)} />
                    <span style={{ flex: 1 }}><b style={{ color: "var(--ink)" }}>{o.name}</b> · {o.provider}{o.eta ? ` · ${o.eta}` : ""}</span>
                    <span className="money" style={{ color: "var(--ink)", fontWeight: 700 }}>{rands(o.priceCents)}</span>
                  </label>
                ))}
              </div>
            )}
          </>
        )}
        <label className="field"><span>Anything we should know? (optional)</span><textarea value={note} onChange={(e) => setNote(e.target.value)} /></label>

        {(eftOffered || shopOffered) && (
          <>
            <h2 style={{ fontSize: "1.15rem", margin: "1.2rem 0 .8rem" }}>How would you like to pay?</h2>
            <div className="choice">
              <label><input type="radio" name="pay" checked={!payOffline} onChange={() => setPayMethod("card")} /><b>Card or instant EFT</b><small>Paid now through our payment partner</small></label>
              {eftOffered && <label><input type="radio" name="pay" checked={payEft} onChange={() => setPayMethod("eft")} /><b>Pay by EFT (bank transfer)</b><small>We show our bank details next · stock held for {config?.eft?.holdHours ?? 48} hours · packed once the payment reflects</small></label>}
              {shopOffered && <label><input type="radio" name="pay" checked={payShop} onChange={() => setPayMethod("shop")} /><b>Pay at the shop (cash on collection)</b><small>Pay in cash when you collect at {config?.payAtShop?.collectPoint ?? point?.name ?? "the shop"} · stock held for {config?.payAtShop?.holdHours ?? 48} hours</small></label>}
            </div>
          </>
        )}
        {!shopOffered && Boolean(config?.payAtShop) && <p className="fine" style={{ marginTop: ".4rem", color: "var(--ink-mute)", fontSize: ".82rem" }}>Choose collection to pay in cash at the shop instead.</p>}

        <button className="btn btn-brass" type="submit" disabled={busy || !canPay} aria-describedby={payBlockedReason ? "pay-blocked" : undefined} style={{ marginTop: ".5rem" }}>{busy ? (payOffline ? "Placing your order…" : "Taking you to payment…") : payEft ? <>Place order · pay {rands(total)} by EFT <Arrow /></> : payShop ? <>Place order · pay {rands(total)} at the shop <Arrow /></> : <>Pay {rands(total)} securely <Arrow /></>}</button>
        {!busy && payBlockedReason && <p id="pay-blocked" className="fine" role="status" style={{ marginTop: ".4rem", color: "var(--ink-soft)" }}>{payBlockedReason}</p>}
        <p className="fine" style={{ marginTop: ".8rem", color: "var(--ink-mute)", fontSize: ".82rem" }}>{payEft ? "Your order is placed now and confirmed once the transfer reflects in our account. Use your order number as the payment reference." : payShop ? "Your order is placed now. Pay in cash when you collect, and quote your order number at the counter." : "You will be taken to our payment partner to pay by card or instant EFT. We never see your card number."}</p>
      </form>

      <aside className="summary">
        <h2>Your order</h2>
        {basket.lines.map((l) => (
          <div className="row" key={l.variantId}><span style={{ color: "var(--ink-soft)" }}>{l.quantity} × {l.title}{l.variantTitle ? ` — ${l.variantTitle}` : ""}</span><span className="money">{rands((l.priceCents ?? 0) * l.quantity)}</span></div>
        ))}
        <div className="row" style={{ borderTop: "1px solid var(--line)", marginTop: ".5rem", paddingTop: ".6rem" }}><span>Items</span><span className="money">{rands(basket.subtotalCents)}</span></div>
        <div style={{ margin: ".4rem 0 .2rem" }}>
          <div className="row" style={{ gap: ".4rem" }}>
            <input value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setDiscount(null); setCodeError(null); }} placeholder="Discount code" aria-label="Discount code" style={{ flex: 1, minWidth: 0, fontFamily: "var(--ui)", padding: ".5rem .7rem", borderRadius: "var(--radius)", border: "1px solid var(--line-strong)", background: "var(--paper-raised)", color: "var(--ink)", minHeight: 40 }} />
            <button type="button" className="btn btn-quiet" style={{ minHeight: 40, padding: ".4rem .9rem" }} onClick={() => void tryCode()}>Apply</button>
          </div>
          {codeError && <div style={{ color: "var(--bad)", fontSize: ".82rem", marginTop: ".3rem" }}>{codeError}</div>}
        </div>
        {discount && discount.itemsOffCents > 0 && <div className="row"><span style={{ color: "var(--good)" }}>{discount.code} · {discount.description}</span><span className="money" style={{ color: "var(--good)" }}>−{rands(discount.itemsOffCents)}</span></div>}
        <div className="row"><span>Delivery{fulfilment === "deliver" && province ? ` · ${province === "Gauteng" ? "Gauteng" : "rest of SA"}` : ""}</span><span className="money">{fulfilment === "collect" ? "free" : quoting ? "pricing…" : quoteFailed ? "couldn't price — retry" : deliveryRefused ? "not available online" : freeOk ? (codeFreeOk && discount ? `free · ${discount.code}` : `free · ${thresholdRands(config?.freeDeliveryOverCents ?? 0)} or more`) : deliveryFee !== null ? rands(deliveryFee) : !province ? "choose a province" : "enter your address"}</span></div>
        <div className="row total"><span>Total</span><span className="money">{rands(total)}</span></div>
        {(() => {
          /** Goods after the code's discount; hidden once the buyer chose collection, and once delivery is already free here. */
          const line = freeOk ? null : freeDeliveryLine(config?.freeDeliveryOverCents, basket.subtotalCents - off, { collect: fulfilment === "collect" });
          return line ? <p className="fine" role="status" style={{ color: "var(--ink)" }}>{line}</p> : null;
        })()}
        <p className="fine">Your order is confirmed the moment payment clears, and you will get an email with the details.</p>
      </aside>
    </div>
  );
}

/* ── Order confirmation ────────────────────────────────────────────────────────────────────── */
/** Where to send EFT proof of payment — the server sends these (shop-eft.ts EFT_PROOF); this covers an older server. */
const EFT_PROOF_FALLBACK = { whatsapp: "079 255 1394", whatsappUrl: "https://wa.me/27792551394", email: "accounts@zsmerch.co.za" };

/** The proof-of-payment instruction, directly under the bank details — prominent, with tappable links. */
function EftProof({ reference, proof }: { reference: string; proof: { whatsapp: string; whatsappUrl: string; email: string } }) {
  return (
    <div role="note" style={{ margin: ".8rem 0 0", padding: ".9rem 1rem", border: "2px solid var(--brass, #9a6b1f)", borderRadius: "var(--radius)", background: "var(--paper-raised)", color: "var(--ink)", maxWidth: "52ch" }}>
      <b>Send your proof of payment</b> by WhatsApp to{" "}
      <a href={proof.whatsappUrl} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 700 }}>{proof.whatsapp}</a> or email{" "}
      <a href={`mailto:${proof.email}?subject=${encodeURIComponent(`Proof of payment ${reference}`)}`} style={{ fontWeight: 700 }}>{proof.email}</a>, with{" "}
      <b className="money">{reference}</b> as the reference.
    </div>
  );
}

function Order({ id, ref, cancelled, go, basket }: { id: string; ref: string; cancelled: boolean; go: (r: Route) => void; basket: Basket }) {
  const [o, setO] = useState<OrderView | null>(null);
  const [busy, setBusy] = useState(true);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    get<OrderView>(`/shop/order/${encodeURIComponent(id)}?key=${encodeURIComponent(ref)}`)
      .then((d) => { if (alive) setO(d); })
      .catch(() => { if (alive) setO(null); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [id, ref, tries]);

  /**
   * ⚠️ A short, bounded poll. The provider's webhook usually lands before the customer does, but
   * not always; the page re-asks a few times before settling on "still waiting" so the customer is
   * not told their paid order is unpaid because they were quicker than a webhook.
   */
  /**
   * 🔴 The basket is cleared HERE — on a confirmed payment, and nowhere else.
   *
   * Checkout used to clear it at the moment it redirected to the gateway, which threw the basket
   * away for every shopper who did not complete: a declined card left them with nothing to retry.
   */
  useEffect(() => {
    if (o?.status === "paid") basket.clear();
  }, [o?.status]);

  /** Ads: Purchase once per order (event_id = order reference); a no-op without consent (store/ads.ts). */
  useEffect(() => {
    if (o?.status === "paid" && o.purchase) void adsPurchase(o.purchase, o.email);
  }, [o?.status, o?.purchase?.eventId]);

  useEffect(() => {
    /** An EFT / pay-at-shop order waits on a person (the bank, the counter), not a webhook: no polling. */
    if (!o || o.status === "paid" || o.payMethod === "eft" || o.payMethod === "shop" || cancelled || tries >= 5) return;
    const t = window.setTimeout(() => setTries((n) => n + 1), 2500);
    return () => window.clearTimeout(t);
  }, [o, cancelled, tries]);

  if (busy && !o) return <div className="empty"><p>Checking your order…</p></div>;
  if (!o) return <div className="empty"><h3>We could not find that order</h3><p>If you have paid, your confirmation email carries everything you need — or contact us with your name.</p><Go to={{ name: "home" }} go={go} className="btn btn-quiet">Back to the shop</Go></div>;

  const paid = o.status === "paid";
  return (
    <div className="wrap two-col">
      <div className="settle">
        {paid ? (
          <>
            <div className="paid-mark"><Check size={28} /></div>
            <h1 style={{ fontSize: "clamp(1.7rem, 1.2rem + 1.6vw, 2.4rem)" }}>Thank you, {o.customerName.split(" ")[0]}. It's paid.</h1>
            <p style={{ color: "var(--ink-soft)", maxWidth: "52ch" }}>
              Order <b className="money">{o.invoiceNumber ?? o.orderId ?? o.id}</b> is confirmed. {o.fulfilment === "collect" ? (o.collect?.readyLabel ? <><CollectLink label={o.collect.readyLabel} mapUrl={o.collect.mapUrl} />. Bring your order number.</> : "Bring your order number when you collect.") : "It ships soon after payment (2–3 working days to main centres, 3–5 to outlying and regional areas) and you will get the tracking by email."}
            </p>
          </>
        ) : o.status === "awaiting_eft" && o.eft ? (
          <>
            <h1 style={{ fontSize: "clamp(1.7rem, 1.2rem + 1.6vw, 2.4rem)" }}>Thank you, {o.customerName.split(" ")[0]}. Pay by EFT</h1>
            <p style={{ color: "var(--ink-soft)", maxWidth: "52ch" }}>
              Your order number is <b className="money">{o.eft.reference}</b>. We'll pack your order once the payment reflects. The same details are in your email.
            </p>
            <h2 style={{ fontSize: "1.05rem", margin: "1.2rem 0 .3rem" }}>Z S MERCH bank details</h2>
            <dl style={{ margin: 0 }}>
              <div className="spec"><dt>Bank</dt><dd>{o.eft.bank}</dd></div>
              <div className="spec"><dt>Account holder</dt><dd>{o.eft.accountHolder}</dd></div>
              <div className="spec"><dt>Account number</dt><dd className="money">{o.eft.accountNumber}</dd></div>
              <div className="spec"><dt>Branch code</dt><dd className="money">{o.eft.branchCode}</dd></div>
              <div className="spec"><dt>Reference</dt><dd><b className="money">{o.eft.reference}</b></dd></div>
              <div className="spec"><dt>Amount</dt><dd><b className="money">{rands(o.eft.amountCents)}</b></dd></div>
            </dl>
            <p style={{ marginTop: ".8rem", color: "var(--ink)" }}>
              Use your order number <b className="money">{o.eft.reference}</b> as the payment reference.
            </p>
            <EftProof reference={o.eft.reference} proof={o.eft.proof ?? EFT_PROOF_FALLBACK} />
            <p className="fine" style={{ marginTop: ".8rem", color: "var(--ink)" }}>
              We are holding your stock until{" "}
              <b>{new Date(o.eft.holdUntil).toLocaleString("en-ZA", { timeZone: "Africa/Johannesburg", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })}</b>; after that the order is cancelled if no payment has reflected.
            </p>
          </>
        ) : o.status === "awaiting_shop" && o.shop ? (
          <>
            <h1 style={{ fontSize: "clamp(1.7rem, 1.2rem + 1.6vw, 2.4rem)" }}>Thank you, {o.customerName.split(" ")[0]}. Pay at the shop</h1>
            <p style={{ color: "var(--ink-soft)", maxWidth: "52ch" }}>
              Your order number is <b className="money">{o.shop.reference}</b>. The same details are in your email.
            </p>
            <div role="note" style={{ margin: "1rem 0 0", padding: ".9rem 1rem", border: "2px solid var(--brass, #9a6b1f)", borderRadius: "var(--radius)", background: "var(--paper-raised)", color: "var(--ink)", maxWidth: "52ch" }}>
              Pay <b className="money">{rands(o.shop.amountCents)}</b> in cash when you collect at <b>{o.shop.collectPoint.name}</b>, quote order number <b className="money">{o.shop.reference}</b>.
            </div>
            <p className="fine" style={{ marginTop: ".8rem", color: "var(--ink)" }}>
              {o.collect?.readyLabel ? <><CollectLink label={o.collect.readyLabel} mapUrl={o.collect.mapUrl} />. </> : null}
              We are holding your stock until{" "}
              <b>{new Date(o.shop.holdUntil).toLocaleString("en-ZA", { timeZone: "Africa/Johannesburg", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })}</b>; after that the order is cancelled if it has not been collected and paid.
            </p>
          </>
        ) : (o.payMethod === "eft" || o.payMethod === "shop") && o.status === "failed" ? (
          <>
            <h1 style={{ fontSize: "clamp(1.7rem, 1.2rem + 1.6vw, 2.4rem)" }}>This order has closed</h1>
            <p style={{ color: "var(--ink-soft)", maxWidth: "52ch" }}>
              {o.payMethod === "shop"
                ? <>Order <b className="money">{o.invoiceNumber ?? o.id}</b> was not collected and paid in time, or it was cancelled, so its stock was released. You are welcome to order again.</>
                : <>No payment reached us for order <b className="money">{o.invoiceNumber ?? o.id}</b> in time, or it was cancelled, so its stock was released. If you have paid, send your proof of payment by WhatsApp to <a href={EFT_PROOF_FALLBACK.whatsappUrl} target="_blank" rel="noopener noreferrer">{EFT_PROOF_FALLBACK.whatsapp}</a> or email <a href={`mailto:${EFT_PROOF_FALLBACK.email}`}>{EFT_PROOF_FALLBACK.email}</a>.</>}
            </p>
            <Go to={{ name: "home" }} go={go} className="btn btn-quiet">Back to the shop</Go>
          </>
        ) : cancelled ? (
          <>
            <h1 style={{ fontSize: "clamp(1.7rem, 1.2rem + 1.6vw, 2.4rem)" }}>Payment was cancelled</h1>
            <p style={{ color: "var(--ink-soft)", maxWidth: "52ch" }}>Nothing has been charged. Your order is saved under <b className="money">{o.id}</b> if you would like to try again.</p>
            <Go to={{ name: "basket" }} go={go} className="btn btn-quiet">Back to the basket</Go>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: "clamp(1.7rem, 1.2rem + 1.6vw, 2.4rem)" }}>Confirming your payment…</h1>
            <p style={{ color: "var(--ink-soft)", maxWidth: "52ch" }}>
              {tries >= 5 ? "It is taking longer than usual. If you have paid, it will show here shortly and your email will confirm it — nothing is lost." : "This normally takes a few seconds."}
            </p>
            {tries >= 5 && <button className="btn btn-quiet" onClick={() => setTries(0)}>Check again</button>}
          </>
        )}
        <h2 style={{ fontSize: "1.05rem", margin: "1.6rem 0 .3rem" }}>Your details</h2>
        <dl style={{ margin: 0 }}>
          <div className="spec"><dt>Your email — order updates go here</dt><dd>{o.email}</dd></div>
          <div className="spec"><dt>{o.fulfilment === "collect" ? "Collect from" : "Deliver to"}</dt><dd>{o.fulfilment === "collect" ? (o.collect ? `${o.collect.name} — ${o.collect.address}` : "") : [o.address?.line1, o.address?.line2, o.address?.city, o.address?.postcode, o.address?.province].filter(Boolean).join(", ")}</dd></div>
        </dl>
        {paid && <p style={{ marginTop: "1.25rem" }}><ChannelButton /></p>}
      </div>
      <aside className="summary">
        <h2>What you ordered</h2>
        {o.lines.map((l, i) => <div className="row" key={i}><span style={{ color: "var(--ink-soft)" }}>{l.quantity} × {l.title}</span><span className="money">{rands(l.lineCents)}</span></div>)}
        <div className="row" style={{ borderTop: "1px solid var(--line)", marginTop: ".5rem", paddingTop: ".6rem" }}><span>Items</span><span className="money">{rands(o.subtotalCents)}</span></div>
        <div className="row"><span>Delivery</span><span className="money">{rands(o.deliveryCents)}</span></div>
        <div className="row total"><span>{paid ? "Paid" : "Total"}</span><span className="money">{rands(o.totalCents)}</span></div>
      </aside>
    </div>
  );
}


/* ── Account: sign in, or sign up as retail or wholesale ───────────────────────────────────────── */
/**
 * Retail asks for four things and is live at once. Wholesale asks for the business as well and
 * becomes a REQUEST — the customer can sign in and shop at retail straight away, and wholesale prices
 * unlock when the owner approves it. The form says so before they type, not after.
 */
const TRADE_TYPES = ["Reseller / online seller", "Spaza or general dealer", "Restaurant or takeaway", "Caterer or events", "Guesthouse or hospitality", "Office or school", "Other business"];
const SPEND_BANDS = ["Under R2 000", "R2 000 – R5 000", "R5 000 – R15 000", "R15 000 – R50 000", "Over R50 000"];

/**
 * "Delete my account" (POST /shop/account/delete): a confirm step that needs DELETE typed, then
 * the server signs every device out. Orders are kept — the copy says so before the button does.
 */
function DeleteAccountSection({ onDeleted }: { onDeleted: (message: string) => void }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    setBusy(true); setError(null);
    try {
      const r = await fetch("/api/shop/account/delete", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ confirm: typed.trim() }) });
      const d = (await r.json().catch(() => ({}))) as { ok?: boolean; message?: string; error?: string };
      if (!r.ok || !d.ok) { setError(d.error ?? "We could not delete your account just now."); return; }
      onDeleted(d.message ?? "Your account has been deleted. Thank you for shopping with us.");
    } catch {
      setError("We could not reach the shop. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section aria-labelledby="delete-account-h" style={{ marginTop: "2.5rem", paddingTop: "1.2rem", borderTop: "1px solid var(--line)" }}>
      <h2 id="delete-account-h" style={{ fontSize: "1.1rem", marginBottom: ".4rem" }}>Delete my account</h2>
      <p style={{ color: "var(--ink-soft)", maxWidth: "56ch", margin: 0 }}>
        This removes your login, saved items and marketing preferences, and signs you out everywhere. Records of your orders, invoices and payments are kept, because the law requires us to keep them.
      </p>
      {!open ? (
        <button type="button" className="btn btn-quiet" style={{ marginTop: ".9rem", color: "var(--bad)" }} onClick={() => setOpen(true)}>Delete my account</button>
      ) : (
        <div role="alertdialog" aria-labelledby="delete-account-h" aria-describedby="delete-account-q" style={{ marginTop: ".9rem", padding: "1rem", border: "1px solid color-mix(in oklab, var(--bad) 30%, transparent)", borderRadius: 10, background: "color-mix(in oklab, var(--bad) 12%, var(--paper-raised))" }}>
          <p id="delete-account-q" style={{ margin: "0 0 .6rem" }}>This cannot be undone. Type <b>DELETE</b> to confirm.</p>
          <label className="field" style={{ maxWidth: "14rem" }}>
            <span>Type DELETE</span>
            <input autoComplete="off" autoCapitalize="characters" value={typed} onChange={(e) => setTyped(e.target.value)} />
          </label>
          {error && <div className="alert" role="alert" style={{ marginTop: ".6rem" }}>{error}</div>}
          <div style={{ display: "flex", gap: ".7rem", marginTop: ".8rem", flexWrap: "wrap" }}>
            <button type="button" className="btn" style={{ background: "var(--bad)", color: "#fff" }} disabled={busy || typed.trim() !== "DELETE"} onClick={() => void confirm()}>
              {busy ? "Deleting…" : "Delete my account"}
            </button>
            <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => { setOpen(false); setTyped(""); setError(null); }}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}

function AccountPage({ tab, go, account, onAccount }: { tab: "in" | "up" | "forgot" | "claim"; go: (r: Route) => void; account: Account | null; onAccount: (a: Account | null) => void }) {
  const [mode, setMode] = useState<"in" | "up" | "forgot" | "claim">(tab);
  const [sent, setSent] = useState<string | null>(null);
  /** Sign-up with an email we already have: nothing was created, a link was mailed instead. */
  const [notice, setNotice] = useState<string | null>(null);
  const [verifyNote, setVerifyNote] = useState<string | null>(null);
  /** Shown on the signed-out page right after "Delete my account" succeeds. */
  const [goodbye, setGoodbye] = useState<string | null>(null);
  const resendVerify = async () => {
    const r = await fetch("/api/shop/verify/resend", { method: "POST" }).catch(() => null);
    const d = r ? ((await r.json()) as { ok?: boolean; error?: string; already?: boolean }) : null;
    setVerifyNote(d?.ok ? (d.already ? "Already confirmed." : "Sent. Check your inbox and spam.") : d?.error ?? "We could not send it right now.");
  };
  const [kind, setKind] = useState<"retail" | "wholesale">("retail");
  const [f, setF] = useState<Record<string, string>>({});
  /** Starts unticked. Consent is given, not defaulted. */
  const [marketing, setMarketing] = useState(false);
  /** Most wholesale buyers are resellers with no registered company (owner, 2026-11-02): VAT and
   *  registration stay hidden unless they say they have one. */
  const [registered, setRegistered] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setMode(tab); setError(null); setSent(null); setNotice(null); }, [tab]);
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (mode === "forgot") {
        const r = await fetch("/api/shop/password/forgot", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ email: f.email }) });
        const d = (await r.json()) as { message?: string };
        setSent(d.message ?? "If that address has an account, a reset link is on its way.");
        return;
      }
      if (mode === "claim") {
        const r = await fetch("/api/shop/claim", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ email: f.email }) });
        const d = (await r.json().catch(() => ({}))) as { message?: string };
        setSent(d.message ?? "If that email is on our records we've sent you a link. It works for 48 hours.");
        return;
      }
      const body = mode === "in"
        ? { email: f.email, password: f.password }
        : {
            kind, name: f.name, email: f.email, phone: f.phone, password: f.password,
            marketingEmail: kind === "retail" && marketing,
            business: kind === "wholesale" ? {
              company: f.company, vatNumber: registered ? f.vat : undefined, registrationNumber: registered ? f.reg : undefined, tradeType: f.tradeType, monthlySpend: f.spend,
              address: { line1: f.line1, line2: f.line2, city: f.city, postcode: f.postcode },
            } : undefined,
          };
      const r = await fetch(mode === "in" ? "/api/shop/login" : "/api/shop/signup", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
      const d = (await r.json()) as { account?: Account; error?: string; existing?: boolean; message?: string };
      if (d.existing && d.message) { setNotice(d.message); return; }
      if (!r.ok || !d.account) { setError(d.error ?? "Something went wrong."); return; }
      onAccount(d.account);
      go({ name: "account" });
    } catch {
      setError("We could not reach the shop. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    await fetch("/api/shop/logout", { method: "POST" }).catch(() => null);
    onAccount(null);
  };

  if (account) {
    const pill = account.kind !== "wholesale" ? null
      : account.tradeStatus === "approved" ? <span className="status-pill good"><Check />Wholesale pricing active</span>
      : account.tradeStatus === "pending" ? <span className="status-pill warn">Wholesale account requested</span>
      : <span className="status-pill">Wholesale request declined</span>;
    return (
      <div className="wrap narrow settle">
        <h1 style={{ fontSize: "clamp(1.7rem, 1.2rem + 1.6vw, 2.4rem)" }}>Hello, {account.name.split(" ")[0]}</h1>
        {pill && <div style={{ margin: ".6rem 0 1rem" }}>{pill}</div>}
        {!account.emailVerified && (
          <div className="alert" style={{ background: "var(--brass-wash)", color: "var(--brass-deep)", border: "1px solid var(--line)" }}>
            Email not confirmed yet. We sent a link to {account.email}.{" "}
            <button type="button" className="remove" style={{ color: "inherit" }} onClick={() => void resendVerify()}>Send it again</button>
            {verifyNote && <div style={{ marginTop: ".3rem" }}>{verifyNote}</div>}
          </div>
        )}
        {account.kind === "wholesale" && account.tradeStatus === "pending" && (
          <p style={{ color: "var(--ink-soft)", maxWidth: "52ch" }}>
            We have {account.company ? <b>{account.company}</b> : "your business"} on file and someone will look at it within a working day. Until then you can shop at retail — the whole store switches to your wholesale prices the moment it is approved.
          </p>
        )}
        <dl style={{ margin: "1rem 0 0" }}>
          <div className="spec"><dt>Email</dt><dd>{account.email}</dd></div>
          {account.phone && <div className="spec"><dt>Phone</dt><dd>{account.phone}</dd></div>}
          {account.company && <div className="spec"><dt>Business</dt><dd>{account.company}</dd></div>}
          <div className="spec"><dt>Prices you see</dt><dd>{account.list === "wholesale" ? "Wholesale" : "Retail"}</dd></div>
        </dl>
        <div style={{ display: "flex", gap: ".7rem", marginTop: "1.5rem" }}>
          <Go to={{ name: "orders" }} go={go} className="btn btn-brass">My orders <Arrow /></Go>
          <Go to={{ name: "home" }} go={go} className="btn btn-quiet">Keep shopping</Go>
          <button className="btn btn-quiet" onClick={() => void signOut()}>Sign out</button>
        </div>
        <DeleteAccountSection onDeleted={(message) => { setGoodbye(message); onAccount(null); }} />
      </div>
    );
  }

  return (
    <div className="wrap narrow settle">
      {goodbye && (
        <div className="alert" role="status" style={{ background: "var(--brass-wash)", color: "var(--brass-deep)", border: "1px solid var(--line)", marginBottom: "1rem" }}>
          {goodbye}
        </div>
      )}
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={mode === "in" || mode === "forgot" || mode === "claim"} onClick={() => go({ name: "account", tab: "in" })}>Sign in</button>
        <button role="tab" aria-selected={mode === "up"} onClick={() => go({ name: "account", tab: "up" })}>Create an account</button>
      </div>
      <p className="fine" style={{ margin: ".6rem 0 1rem" }}>Bought as a guest? <Go to={{ name: "track" }} go={go}>Track an order</Go></p>
      {mode === "forgot" || mode === "claim" ? (
        <form onSubmit={(e) => void submit(e)}>
          {mode === "forgot" ? (
            <>
              <h1 style={{ fontSize: "clamp(1.5rem, 1.1rem + 1.4vw, 2.1rem)", marginBottom: ".4rem" }}>Forgot your password</h1>
              <p style={{ color: "var(--ink-soft)" }}>Tell us the email on the account and we will send a link to choose a new one. It works for one hour.</p>
            </>
          ) : (
            <>
              <h1 style={{ fontSize: "clamp(1.5rem, 1.1rem + 1.4vw, 2.1rem)", marginBottom: ".4rem" }}>Set up your login</h1>
              <p style={{ color: "var(--ink-soft)" }}>Bought from us before, in the shop or online? Tell us the email you gave us and we will send a link to choose a password. Your login is then ready on the details we already have.</p>
            </>
          )}
          {sent ? (
            <div className="alert good">{sent}</div>
          ) : (
            <>
              <label className="field"><span>Email</span><input type="email" value={f.email ?? ""} onChange={set("email")} autoComplete="email" required /></label>
              <button className="btn btn-brass" type="submit" disabled={busy}>{busy ? "One moment…" : "Send the link"}</button>
            </>
          )}
          <p className="fine" style={{ marginTop: "1rem" }}><Go to={{ name: "account", tab: "in" }} go={go}>Back to sign in</Go></p>
        </form>
      ) : (
      <form onSubmit={(e) => void submit(e)}>
        {error && <div className="alert bad" role="alert">{error}</div>}
        {notice && <div className="alert good" role="status">{notice}</div>}
        {mode === "up" && (
          <>
            <h1 style={{ fontSize: "clamp(1.5rem, 1.1rem + 1.4vw, 2.1rem)", marginBottom: ".4rem" }}>Create an account</h1>
            <p style={{ color: "var(--ink-soft)" }}>Faster checkout, your order history in one place — and wholesale pricing if you buy for a business.</p>
            <div className="choice">
              <label><input type="radio" name="kind" checked={kind === "retail"} onChange={() => setKind("retail")} /><b>I'm shopping for myself</b><small>Retail prices, ready in a minute</small></label>
              <label><input type="radio" name="kind" checked={kind === "wholesale"} onChange={() => setKind("wholesale")} /><b>I'm buying for a business</b><small>Apply for wholesale pricing — we check and approve it</small></label>
            </div>
            <label className="field"><span>Your name</span><input value={f.name ?? ""} onChange={set("name")} autoComplete="name" required /></label>
          </>
        )}
        <label className="field"><span>Email</span><input type="email" value={f.email ?? ""} onChange={set("email")} autoComplete="email" required /></label>
        {mode === "up" && (
          <label className="field"><span>Phone{kind === "wholesale" ? "" : " (optional)"}</span><input type="tel" value={f.phone ?? ""} onChange={set("phone")} autoComplete="tel" required={kind === "wholesale"} /></label>
        )}
        <label className="field"><span>Password{mode === "up" ? " (at least 8 characters)" : ""}</span><input type="password" value={f.password ?? ""} onChange={set("password")} autoComplete={mode === "up" ? "new-password" : "current-password"} minLength={mode === "up" ? 8 : undefined} required /></label>

        {mode === "up" && kind === "retail" && (
          <label className="consent">
            <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />
            <span>Email me about specials and new stock. No more than a couple a month, and you can stop any time.</span>
          </label>
        )}
        {mode === "up" && kind === "wholesale" && (
          <div className="fieldset">
            <h3>About your business</h3>
            <p className="fine" style={{ color: "var(--ink-mute)", fontSize: ".82rem", marginTop: 0 }}>Reselling from home or a stall? That's fine — you don't need a registered company.</p>
            <label className="field"><span>Business or trading name (optional)</span><input value={f.company ?? ""} onChange={set("company")} autoComplete="organization" placeholder="Leave blank to use your name" /></label>
            <label className="consent">
              <input type="checkbox" checked={registered} onChange={(e) => setRegistered(e.target.checked)} />
              <span>I have a registered company</span>
            </label>
            {registered && (
              <div className="field-row">
                <label className="field"><span>VAT number (optional)</span><input value={f.vat ?? ""} onChange={set("vat")} inputMode="numeric" placeholder="4XXXXXXXXX" /></label>
                <label className="field"><span>Company registration (optional)</span><input value={f.reg ?? ""} onChange={set("reg")} /></label>
              </div>
            )}
            <div className="field-row">
              <label className="field"><span>What kind of business</span>
                <select value={f.tradeType ?? ""} onChange={set("tradeType")} required><option value="">Choose…</option>{TRADE_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
              </label>
              <label className="field"><span>Roughly how much a month</span>
                <select value={f.spend ?? ""} onChange={set("spend")} required><option value="">Choose…</option>{SPEND_BANDS.map((t) => <option key={t}>{t}</option>)}</select>
              </label>
            </div>
            <AddressSearch endpoint="/api/shop/address" className="field" label="Your address" value={f.line1 ?? ""} onChange={(v) => setF((p) => ({ ...p, line1: v }))} autoComplete="address-line1" placeholder="Start typing the address" required
              onPick={(a) => setF((p) => ({ ...p, line1: [a.street || a.formatted.split(",")[0], a.suburb].filter(Boolean).join(", "), line2: a.unit, city: a.city, postcode: a.postcode }))} />
            <label className="field"><span>Unit, floor or building (optional)</span><input value={f.line2 ?? ""} onChange={set("line2")} autoComplete="address-line2" /></label>
            <div className="field-row">
              <label className="field"><span>City or town</span><input value={f.city ?? ""} onChange={set("city")} autoComplete="address-level2" required /></label>
              <label className="field"><span>Postal code</span><input value={f.postcode ?? ""} onChange={set("postcode")} autoComplete="postal-code" inputMode="numeric" required /></label>
            </div>
            <p className="fine" style={{ color: "var(--ink-mute)", fontSize: ".82rem" }}>A person checks every wholesale application, usually within a working day. You can shop at retail in the meantime.</p>
          </div>
        )}

        <button className="btn btn-brass" type="submit" disabled={busy} style={{ marginTop: ".5rem" }}>
          {busy ? "One moment…" : mode === "in" ? "Sign in" : kind === "wholesale" ? "Apply for a wholesale account" : "Create my account"}
        </button>
        {mode === "in" && (
          <>
            <p className="fine" style={{ marginTop: "1rem" }}><Go to={{ name: "account", tab: "forgot" }} go={go}>Forgot your password?</Go></p>
            <p className="fine" style={{ marginTop: ".4rem" }}><Go to={{ name: "account", tab: "claim" }} go={go}>Bought from us before? Set up your login</Go></p>
          </>
        )}
      </form>
      )}
    </div>
  );
}

/* ── Email confirmation and password reset landing pages ───────────────────────────────────── */
function VerifyPage({ token, go, onAccount }: { token: string; go: (r: Route) => void; onAccount: (a: Account | null) => void }) {
  const [state, setState] = useState<"working" | "ok" | "bad">("working");
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    fetch("/api/shop/verify", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ token }) })
      .then(async (r) => {
        const d = (await r.json()) as { ok?: boolean; error?: string; account?: Account };
        if (!alive) return;
        if (r.ok && d.ok) { setState("ok"); if (d.account) onAccount(d.account); }
        else { setState("bad"); setMessage(d.error ?? null); }
      })
      .catch(() => { if (alive) setState("bad"); });
    return () => { alive = false; };
  }, [token, onAccount]);
  return (
    <div className="wrap narrow settle">
      {state === "working" && <p>Confirming your email…</p>}
      {state === "ok" && (
        <>
          <div className="paid-mark"><Check size={28} /></div>
          <h1 style={{ fontSize: "clamp(1.5rem, 1.1rem + 1.4vw, 2.1rem)" }}>Email confirmed</h1>
          <p style={{ color: "var(--ink-soft)" }}>Thank you. Everything on your account now goes to a confirmed address.</p>
          <Go to={{ name: "home" }} go={go} className="btn btn-brass">Keep shopping <Arrow /></Go>
        </>
      )}
      {state === "bad" && (
        <>
          <h1 style={{ fontSize: "clamp(1.5rem, 1.1rem + 1.4vw, 2.1rem)" }}>That link did not work</h1>
          <p style={{ color: "var(--ink-soft)" }}>{message ?? "It may have expired. Sign in and ask for a new one from your account page."}</p>
          <Go to={{ name: "account", tab: "in" }} go={go} className="btn btn-quiet">Sign in</Go>
        </>
      )}
    </div>
  );
}

/**
 * The "set up your login" link from the mail. Choosing a password creates the login on the
 * customer we already have (shop-claim.ts) and signs them straight in — the link proved the address.
 */
function WelcomePage({ token, go, onAccount }: { token: string; go: (r: Route) => void; onAccount: (a: Account | null) => void }) {
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== again) { setError("The two passwords do not match."); return; }
    setBusy(true); setError(null);
    try {
      const r = await fetch("/api/shop/claim/complete", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ token, password }) });
      const d = (await r.json()) as { ok?: boolean; error?: string; account?: Account };
      if (r.ok && d.ok && d.account) { onAccount(d.account); go({ name: "account" }); }
      else setError(d.error ?? "That did not work.");
    } catch {
      setError("We could not reach the shop. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="wrap narrow settle">
      <form onSubmit={(e) => void submit(e)}>
        <h1 style={{ fontSize: "clamp(1.5rem, 1.1rem + 1.4vw, 2.1rem)", marginBottom: ".4rem" }}>Set up your login</h1>
        <p style={{ color: "var(--ink-soft)" }}>Choose a password and you are signed in. Next time, sign in with your email and this password.</p>
        {error && <div className="alert bad" role="alert">{error}</div>}
        <label className="field"><span>Password (at least 8 characters)</span><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" minLength={8} required /></label>
        <label className="field"><span>Once more</span><input type="password" value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="new-password" minLength={8} required /></label>
        <button className="btn btn-brass" type="submit" disabled={busy}>{busy ? "One moment…" : "Save and sign in"}</button>
        <p className="fine" style={{ marginTop: "1rem" }}><Go to={{ name: "account", tab: "claim" }} go={go}>Link not working? Ask for a new one</Go></p>
      </form>
    </div>
  );
}

function ResetPage({ token, go }: { token: string; go: (r: Route) => void }) {
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== again) { setError("The two passwords do not match."); return; }
    setBusy(true); setError(null);
    try {
      const r = await fetch("/api/shop/password/reset", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ token, password }) });
      const d = (await r.json()) as { ok?: boolean; error?: string };
      if (r.ok && d.ok) setDone(true); else setError(d.error ?? "That did not work.");
    } catch {
      setError("We could not reach the shop. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="wrap narrow settle">
      {done ? (
        <>
          <div className="paid-mark"><Check size={28} /></div>
          <h1 style={{ fontSize: "clamp(1.5rem, 1.1rem + 1.4vw, 2.1rem)" }}>Password changed</h1>
          <p style={{ color: "var(--ink-soft)" }}>Every device has been signed out. Sign in with the new password.</p>
          <Go to={{ name: "account", tab: "in" }} go={go} className="btn btn-brass">Sign in <Arrow /></Go>
        </>
      ) : (
        <form onSubmit={(e) => void submit(e)}>
          <h1 style={{ fontSize: "clamp(1.5rem, 1.1rem + 1.4vw, 2.1rem)", marginBottom: ".4rem" }}>Choose a new password</h1>
          {error && <div className="alert bad" role="alert">{error}</div>}
          <label className="field"><span>New password (at least 8 characters)</span><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" minLength={8} required /></label>
          <label className="field"><span>Once more</span><input type="password" value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="new-password" minLength={8} required /></label>
          <button className="btn btn-brass" type="submit" disabled={busy}>{busy ? "One moment…" : "Save password"}</button>
        </form>
      )}
    </div>
  );
}

/* ── Shell ─────────────────────────────────────────────────────────────────────────────────── */
function Shop() {
  const [route, go] = useRoute();
  const [categories, setCategories] = useState<CategorySummary[]>([]);
  const [nav, setNav] = useState<NavNode[]>([]);
  const [config, setConfig] = useState<ShopConfig | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [accountReady, setAccountReady] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const basket = useBasket();

  useEffect(() => {
    get<{ categories: CategorySummary[] }>("/shop/categories").then((d) => setCategories(d.categories)).catch(() => setCategories([]));
    get<NavResponse>("/shop/nav").then((d) => setNav(d.tree)).catch(() => setNav([]));
    get<ShopConfig>("/shop/config").then((c) => { setConfig(c); configureAds(c.ads); }).catch(() => setConfig(null));
    get<{ account: Account | null }>("/shop/me").then((d) => setAccount(d.account)).catch(() => setAccount(null)).finally(() => setAccountReady(true));
  }, []);

  /** Pages with their own data (department, product, search, collections…) set their own title once loaded. */
  useEffect(() => {
    const titles: Partial<Record<Route["name"], string>> = {
      basket: "Cart", checkout: "Checkout", order: "Your order", account: "Your account", reset: "New password", verify: "Confirm email",
    };
    const t = titles[route.name];
    if (t) setSeo(t, null);
  }, [route]);

  /** Ads: a page view per in-shop navigation after the first (no-op until configured + accepted). */
  const [firstRoute] = useState(route);
  useEffect(() => {
    if (route !== firstRoute) adsPageView();
  }, [route, firstRoute]);

  return (
    <>
      <a className="skip" href="#main" onClick={(e) => { e.preventDefault(); document.getElementById("main")?.focus(); }}>Skip to content</a>
      <AppBanner />
      <Header roots={nav} go={go} route={route} basket={basket} account={account} config={config} onCart={() => setCartOpen(true)} />
      <main id="main" tabIndex={-1}>
        {route.name === "home" && <HomePage nav={nav} go={go} account={account} />}
        {route.name === "dept" && <DepartmentRoute id={route.id} qs={route.qs ?? ""} go={go} nav={nav} account={account} />}
        {route.name === "category" && <CollectionRoute handle={route.handle} qs={route.qs ?? ""} go={go} nav={nav} account={account} />}
        {route.name === "search" && <SearchRoute qs={route.qs ?? ""} go={go} nav={nav} account={account} />}
        {route.name === "clearance" && <ClearanceRoute qs={route.qs ?? ""} go={go} nav={nav} account={account} />}
        {route.name === "campaign" && <Suspense fallback={<div className="wrap narrow settle"><p>Loading…</p></div>}><CampaignPage slug={route.slug} go={go} account={account} /></Suspense>}
        {route.name === "campaigns" && <Suspense fallback={<div className="wrap narrow settle"><p>Loading…</p></div>}><CampaignsPage go={go} /></Suspense>}
        {route.name === "bundles" && <Suspense fallback={<div className="wrap narrow settle"><p>Loading…</p></div>}><BundlesPage theme={route.theme} go={go} /></Suspense>}
        {route.name === "guides" && <Suspense fallback={<div className="wrap narrow settle"><p>Loading…</p></div>}><GuidesPage go={go} /></Suspense>}
        {route.name === "guide" && <Suspense fallback={<div className="wrap narrow settle"><p>Loading…</p></div>}><GuidePage slug={route.slug} go={go} /></Suspense>}
        {route.name === "bundle" && <Suspense fallback={<div className="wrap narrow settle"><p>Loading…</p></div>}><BundlePage slug={route.slug} go={go} basket={basket} onAdded={() => setCartOpen(true)} /></Suspense>}
        {route.name === "collections" && <CollectionsPage categories={categories} go={go} />}
        {route.name === "faq" && <FaqPage config={config} go={go} />}
        {route.name === "contact" && <ContactPage config={config} go={go} />}
        {route.name === "about" && <AboutPage config={config} go={go} />}
        {route.name === "product" && <ProductPage handle={route.handle} go={go} basket={basket} account={account} onAdded={() => setCartOpen(true)} freeDeliveryOverCents={config?.freeDeliveryOverCents ?? null} />}
        {route.name === "basket" && <BasketPage basket={basket} go={go} config={config} trade={account?.list === "wholesale"} />}
        {route.name === "checkout" && <Checkout basket={basket} go={go} config={config} account={account} />}
        {route.name === "account" && <AccountPage tab={route.tab ?? "in"} go={go} account={account} onAccount={setAccount} />}
        {route.name === "verify" && <VerifyPage token={route.token} go={go} onAccount={setAccount} />}
        {route.name === "reset" && <ResetPage token={route.token} go={go} />}
        {route.name === "welcome" && <WelcomePage token={route.token} go={go} onAccount={setAccount} />}
        {route.name === "order" && <Order id={route.id} ref={route.ref} cancelled={route.cancelled} go={go} basket={basket} />}
        {route.name === "orders" && <Suspense fallback={<div className="wrap narrow settle"><p>Loading…</p></div>}><OrdersPage id={route.id} go={go} account={account} /></Suspense>}
        {route.name === "track" && <Suspense fallback={<div className="wrap narrow settle"><p>Loading…</p></div>}><TrackPage go={go} /></Suspense>}
        {route.name === "review" && <Suspense fallback={<div className="wrap narrow settle"><p>Opening your order…</p></div>}><ReviewOrderPage token={route.token} go={go} /></Suspense>}
        {route.name === "policy" && <Suspense fallback={<div className="wrap narrow settle"><p>Loading…</p></div>}><PolicyPage handle={route.handle} go={go} /></Suspense>}
        {route.name === "notfound" && <NotFoundPage go={go} />}
      </main>
      <Footer go={go} config={config} />
      <SubscribePopup route={route} account={account} accountReady={accountReady} />
      <ConsentBanner ads={config?.ads} />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} basket={basket} go={go} freeDeliveryOverCents={account?.list === "wholesale" ? null : (config?.freeDeliveryOverCents ?? null)} />
    </>
  );
}

/**
 * ── A render error is a sentence, never a blank page ─────────────────────────────────────────
 * createRoot CLEARS the server-rendered content on mount, so an uncaught throw anywhere below
 * would leave a customer looking at white. This catches it and offers the one thing that fixes
 * most of them (a reload — a stale tab after a deploy is the usual cause), with a way home.
 */
class ShopErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  componentDidCatch(error: unknown): void {
    console.error("shop_render_failed", error);
  }
  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <main id="main" className="empty" role="alert">
        <h1>Something went wrong loading the shop</h1>
        <p>Please reload the page. If it keeps happening, go back to the shop's front page.</p>
        <p>
          <button type="button" className="btn" onClick={() => window.location.reload()}>Reload</button>{" "}
          <a className="btn btn-quiet" href={pathFor({ name: "home" })}>Back to the shop</a>
        </p>
      </main>
    );
  }
}

/** Shop events feed the ad tags (consent- and id-gated inside adsEvent). Registered once, at startup. */
onTrack(trackToAds);

createRoot(document.getElementById("root")!).render(<StrictMode><ShopErrorBoundary><Shop /></ShopErrorBoundary></StrictMode>);
